/**
 * Live, odometer-animated countdown for a booking-request deadline.
 *
 * A pending request expires if the host doesn't approve it in time
 * (`offer_expiration_date` = server `min(created + 48h, pickup − 1h)`). The
 * earlier version rendered a BUCKETED relative label ("Expires in 2 days") via
 * `getQuoteCountdown`, which only changed once a day at the days granularity —
 * so a deadline still days out looked completely frozen and the "live odometer"
 * never visibly moved.
 *
 * This renders a true ticking clock instead — `Dd HH:MM:SS` (the days segment
 * drops once under a day) — re-evaluated every second. Each numeric segment is
 * its own `RollingNumber` in `roll="nearest"` mode, so a counting-DOWN digit
 * clicks gently the short way over from its previous value (e.g. 7→6, or the
 * 0→9 borrow) rather than spinning almost a full turn forward every second.
 * Only the segment whose value actually changed animates: the seconds reel rolls
 * every second, the minutes reel once a minute, and so on.
 * The clock is locale-neutral (digits + colons); only the short "Expires in"
 * label is translated.
 *
 * Edge handling (via `getCountdownParts`):
 *   - missing / unparseable / already-expired → renders nothing (the parent's
 *     status logic owns the terminal "Expired" state), so a viewer never sees a
 *     zero or negative clock.
 *
 * Two presentation wrappers share the same ticking core:
 *   - `OfferExpiryCountdown`        — host Booking Management list (amber).
 *   - `RenterOfferExpiryCountdown`  — renter Trips list (brand pill, keeps the
 *     "{host} has …" copy with the live clock spliced in at the time slot).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock, Bell } from "lucide-react";

import { cn } from "@/lib/utils";
import { getCountdownParts, type CountdownParts } from "@/lib/trip-datetime";
import { RollingNumber } from "@/components/ui/rolling-number";

// Tick every second so the seconds figure visibly counts down and the odometer
// rolls on each change. Each RollingNumber only re-animates when ITS own value
// changes, so a per-second tick costs one small reel roll, not a full reflow.
const TICK_MS = 1_000;

// A character that will never appear in translated copy, used to mark where the
// live clock belongs inside a localized sentence (renter copy).
const VALUE_SENTINEL = "\u0000";

/**
 * Re-evaluate the remaining time once a second. Returns the segment breakdown,
 * or null once the deadline is missing / expired so the caller renders nothing.
 */
function useCountdownParts(expiresAt: string | null | undefined): CountdownParts | null {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);
  return getCountdownParts(expiresAt, now);
}

/**
 * The ticking `Dd HH:MM:SS` clock itself. Each segment is an independent
 * RollingNumber so only the digit(s) that change roll. Seconds are slightly
 * de-emphasized so the eye rests on the meaningful HH:MM while they tick.
 */
function CountdownClock({ parts }: { parts: CountdownParts }) {
  const two = (n: number) => String(Math.round(n)).padStart(2, "0");
  return (
    <span
      className="inline-flex items-baseline tabular-nums"
      data-testid="countdown-clock"
    >
      {parts.days > 0 && (
        <span className="mr-1 inline-flex items-baseline">
          <RollingNumber value={parts.days} format={(n) => String(Math.round(n))} roll="nearest" />
          <span>d</span>
        </span>
      )}
      <RollingNumber value={parts.hours} format={two} roll="nearest" />
      <span className="mx-px">:</span>
      <RollingNumber value={parts.minutes} format={two} roll="nearest" />
      <span className="mx-px">:</span>
      <RollingNumber value={parts.seconds} format={two} className="opacity-60" roll="nearest" />
    </span>
  );
}

/** Host Booking Management list — amber, inline, with a clock glyph. */
export function OfferExpiryCountdown({
  expiresAt,
  className,
}: {
  expiresAt: string | null | undefined;
  className?: string;
}) {
  const { t } = useTranslation();
  const parts = useCountdownParts(expiresAt);
  // Render NOTHING (not even the icon) when there's nothing meaningful to show,
  // so a pending row whose deadline already lapsed — the brief window before the
  // expiry cron flips its status — never leaves a stray clock on the card.
  if (!parts) return null;

  const title = expiresAt ? new Date(expiresAt).toLocaleString() : undefined;
  return (
    <p
      className={cn(
        "mt-2.5 flex items-center gap-1.5 text-xs font-semibold text-amber-600",
        className,
      )}
      title={title}
      data-testid="offer-expiry-countdown"
    >
      <Clock className="h-3 w-3 shrink-0" />
      <span className="inline-flex items-baseline gap-1">
        {t("owner_booking.list.offer_expires_in", "Expires in")}
        <CountdownClock parts={parts} />
      </span>
    </p>
  );
}

/**
 * Renter Trips list — brand pill that keeps the existing personalized copy
 * ("{host} has …" / "Host has …") but splices the LIVE clock into the slot
 * where the static "5 hours" text used to sit, so the renter sees the same
 * ticking deadline the host does. Renders nothing once the deadline lapses.
 */
export function RenterOfferExpiryCountdown({
  expiresAt,
  ownerFirstName,
  className,
}: {
  expiresAt: string | null | undefined;
  ownerFirstName?: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const parts = useCountdownParts(expiresAt);
  if (!parts) return null;

  const key = ownerFirstName ? "trips.host_has_time" : "trips.host_generic_time";
  const text = t(key, { name: ownerFirstName ?? "", time: VALUE_SENTINEL });
  const [before, after = ""] = text.split(VALUE_SENTINEL);

  return (
    <div
      className={cn(
        "mt-3 inline-flex items-center gap-2 rounded-full bg-brand-tint px-3 py-1.5 text-[12px] font-medium text-brand",
        className,
      )}
      data-testid="renter-offer-expiry-countdown"
    >
      <Bell className="h-3.5 w-3.5 shrink-0" />
      <span className="inline-flex items-baseline gap-1">
        {before}
        <CountdownClock parts={parts} />
        {after}
      </span>
    </div>
  );
}
